import React from 'react';
import { FiStar, FiMapPin, FiClock, FiDollarSign } from 'react-icons/fi';
import './Components.css';

// ─── Badge ──────────────────────────────────────────────────────
export function Badge({ children, variant = 'default', selected, onClick, size = 'md' }) {
  return (
    <span
      className={`badge badge--${variant} badge--${size} ${selected ? 'badge--selected' : ''} ${onClick ? 'badge--clickable' : ''}`}
      onClick={onClick}
    >
      {children}
    </span>
  );
}

// ─── Button ─────────────────────────────────────────────────────
export function Button({ children, onClick, variant = 'primary', disabled, size = 'md', className = '', type = 'button', fullWidth }) {
  return (
    <button
      type={type}
      onClick={disabled ? undefined : onClick}
      disabled={disabled}
      className={`btn btn--${variant} btn--${size} ${fullWidth ? 'btn--full' : ''} ${className}`}
    >
      {children}
    </button>
  );
}

// ─── Input ──────────────────────────────────────────────────────
export function Input({ label, error, ...props }) {
  return (
    <div className="input-group">
      {label && <label className="input-label">{label}</label>}
      <input className={`input-field ${error ? 'input-field--error' : ''}`} {...props} />
      {error && <span className="input-error">{error}</span>}
    </div>
  );
}

// ─── TextArea ───────────────────────────────────────────────────
export function TextArea({ label, error, ...props }) {
  return (
    <div className="input-group">
      {label && <label className="input-label">{label}</label>}
      <textarea className={`input-field input-textarea ${error ? 'input-field--error' : ''}`} {...props} />
      {error && <span className="input-error">{error}</span>}
    </div>
  );
}

// ─── Card ───────────────────────────────────────────────────────
export function Card({ children, className = '', hover, onClick }) {
  return (
    <div className={`card ${hover ? 'card--hover' : ''} ${className}`} onClick={onClick}>
      {children}
    </div>
  );
}

// ─── Avatar ─────────────────────────────────────────────────────
export function Avatar({ initials, size = 'md', color }) {
  const colors = ['#6366f1', '#8b5cf6', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#14b8a6'];
  const bg = color || colors[initials.charCodeAt(0) % colors.length];
  return (
    <div className={`avatar avatar--${size}`} style={{ background: bg }}>
      {initials}
    </div>
  );
}

// ─── Star Rating ────────────────────────────────────────────────
export function StarRating({ rating, count }) {
  return (
    <div className="star-rating">
      <FiStar className="star-icon" />
      <span className="star-value">{rating}</span>
      {count !== undefined && <span className="star-count">({count} reviews)</span>}
    </div>
  );
}

// ─── Coach Card ─────────────────────────────────────────────────
export function CoachCard({ coach, onViewProfile, onBookSession }) {
  return (
    <Card className="coach-card" hover>
      <div className="coach-card__header">
        <Avatar initials={coach.avatar || coach.name.split(' ').map(n => n[0]).join('')} size="lg" />
        <div className="coach-card__info">
          <h3 className="coach-card__name">{coach.name}</h3>
          <StarRating rating={coach.rating} count={coach.totalRatings} />
          <div className="coach-card__meta">
            {coach.location && (
              <span className="coach-card__meta-item">
                <FiMapPin size={12} /> {coach.location}
              </span>
            )}
            <span className="coach-card__meta-item">
              <FiClock size={12} /> {coach.totalSessions} sessions
            </span>
          </div>
        </div>
      </div>
      <div className="coach-card__expertise">
        {coach.expertise?.map((e) => (
          <Badge key={e} variant="primary" size="sm">{e}</Badge>
        ))}
      </div>
      <p className="coach-card__bio">{coach.bio}</p>
      {coach.coachingInterests && (
        <p className="coach-card__interests">"{coach.coachingInterests}"</p>
      )}
      <div className="coach-card__footer">
        <div className="coach-card__rate">
          <FiDollarSign size={16} />
          <span className="coach-card__rate-value">${coach.rate}</span>
          <span className="coach-card__rate-label">/hr</span>
        </div>
        <div className="coach-card__actions">
          <Button variant="outline" size="sm" onClick={() => onViewProfile(coach._id)}>
            View Profile
          </Button>
          <Button size="sm" onClick={() => onBookSession(coach._id)}>
            Book Session
          </Button>
        </div>
      </div>
    </Card>
  );
}

// ─── Slot Badge (for availability display) ──────────────────────
export function SlotBadge({ time, selected, onClick, booked }) {
  return (
    <button
      className={`slot-badge ${selected ? 'slot-badge--selected' : ''} ${booked ? 'slot-badge--booked' : ''}`}
      onClick={booked ? undefined : onClick}
      disabled={booked}
    >
      {time}
    </button>
  );
}

// ─── Day Label ──────────────────────────────────────────────────
export function DayLabel({ label, date }) {
  const isToday = label === 'Today';
  const isTomorrow = label === 'Tomorrow';
  return (
    <div className="day-label">
      <span className={`day-label__tag ${isToday ? 'day-label__tag--today' : isTomorrow ? 'day-label__tag--tomorrow' : ''}`}>
        {label}
      </span>
      {date && <span className="day-label__date">{date}</span>}
    </div>
  );
}

// ─── Loading Spinner ────────────────────────────────────────────
export function Spinner({ size = 'md' }) {
  return (
    <div className={`spinner spinner--${size}`}>
      <div className="spinner__ring" />
    </div>
  );
}

// ─── Empty State ────────────────────────────────────────────────
export function EmptyState({ icon, title, description, action }) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state__icon">{icon}</div>}
      <h3 className="empty-state__title">{title}</h3>
      {description && <p className="empty-state__desc">{description}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  );
}

// ─── Stats Card ─────────────────────────────────────────────────
export function StatCard({ value, label, icon, color = '#6366f1' }) {
  return (
    <Card className="stat-card">
      <div className="stat-card__icon" style={{ background: `${color}15`, color }}>
        {icon}
      </div>
      <div className="stat-card__value" style={{ color }}>{value}</div>
      <div className="stat-card__label">{label}</div>
    </Card>
  );
}
